import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/solid';
import { getTemplates } from '../../services/templateService';

const TemplateSelector = ({
  selectedTemplate,
  onSelectTemplate,
  isPro = false,
  className = ''
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await getTemplates();
        if (response.success) {
          setTemplates(response.data);
        }
      } catch (error) {
        console.error('Failed to load templates:', error);
        // Fallback templates
        setTemplates([
          { id: 'traditional', name: 'Traditional', description: 'Classic, ATS-friendly format', proOnly: false },
          { id: 'modern', name: 'Modern', description: 'Clean, contemporary design', proOnly: false },
          { id: 'creative', name: 'Creative', description: 'Stand out with style', proOnly: true }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  if (loading) {
    return (
      <div className={`flex gap-3 ${className}`}>
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="w-24 h-32 bg-gray-200 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  return (
    <div className={`flex gap-3 overflow-x-auto pb-2 ${className}`}>
      {templates.map((template) => {
        const isSelected = selectedTemplate === template.id;
        const isLocked = template.proOnly && !isPro;

        return (
          <button
            key={template.id}
            onClick={() => !isLocked && onSelectTemplate(template.id)}
            disabled={isLocked}
            className={`
              relative flex-shrink-0 w-24 rounded-lg border-2 transition-all
              ${isSelected
                ? 'border-primary-600 ring-2 ring-primary-200'
                : 'border-gray-200 hover:border-gray-300'
              }
              ${isLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            {/* Template preview thumbnail */}
            <div className="aspect-[8.5/11] bg-white rounded-t-lg p-2 overflow-hidden">
              <TemplateThumbnail templateId={template.id} />
            </div>

            {/* Template info */}
            <div className="p-2 text-center">
              <p className="text-xs font-medium text-gray-900 truncate">
                {template.name}
              </p>
            </div>

            {/* Selected indicator */}
            {isSelected && (
              <div className="absolute -top-2 -right-2">
                <CheckCircleIcon className="w-5 h-5 text-primary-600 bg-white rounded-full" />
              </div>
            )}

            {/* Pro badge */}
            {template.proOnly && (
              <div className="absolute top-1 left-1">
                <span className={`
                  inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium
                  ${isPro ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'}
                `}>
                  <SparklesIcon className="w-3 h-3" />
                  PRO
                </span>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

// Mini preview thumbnails for each template
const TemplateThumbnail = ({ templateId }) => {
  const thumbnails = {
    traditional: (
      <div className="space-y-1">
        <div className="h-2 w-3/4 bg-gray-300 rounded mx-auto" />
        <div className="h-1 w-1/2 bg-gray-200 rounded mx-auto" />
        <div className="mt-2 space-y-0.5">
          <div className="h-1 w-full bg-gray-200 rounded" />
          <div className="h-1 w-full bg-gray-200 rounded" />
          <div className="h-1 w-3/4 bg-gray-200 rounded" />
        </div>
      </div>
    ),
    modern: (
      <div className="flex gap-1">
        <div className="w-1/3 space-y-1">
          <div className="h-3 w-3 bg-primary-200 rounded-full mx-auto" />
          <div className="h-1 w-full bg-gray-200 rounded" />
          <div className="h-1 w-full bg-gray-200 rounded" />
        </div>
        <div className="w-2/3 space-y-1 pt-1">
          <div className="h-1.5 w-3/4 bg-primary-300 rounded" />
          <div className="h-1 w-full bg-gray-200 rounded" />
          <div className="h-1 w-full bg-gray-200 rounded" />
        </div>
      </div>
    ),
    creative: (
      <div className="space-y-1">
        <div className="h-4 bg-gradient-to-r from-primary-400 to-primary-200 rounded" />
        <div className="flex gap-1">
          <div className="w-1/2 space-y-0.5">
            <div className="h-1 w-full bg-gray-200 rounded" />
            <div className="h-1 w-3/4 bg-gray-200 rounded" />
          </div>
          <div className="w-1/2 space-y-0.5">
            <div className="h-1 w-full bg-gray-200 rounded" />
            <div className="h-1 w-3/4 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    )
  };

  return thumbnails[templateId] || thumbnails.traditional;
};

TemplateThumbnail.propTypes = {
  templateId: PropTypes.string.isRequired
};

TemplateSelector.propTypes = {
  selectedTemplate: PropTypes.string.isRequired,
  onSelectTemplate: PropTypes.func.isRequired,
  isPro: PropTypes.bool,
  className: PropTypes.string
};

export default TemplateSelector;
